import { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '@/store/authSlice';
import { api } from '@/store/api';
import { clearAuthStorage, getAccessToken } from '../utils/authStorage';
import { setLanguage } from '@/store/languageSlice';
import {
  Globe, User, Home, Menu, X, Coins,
  FileText,
  LogOut, Plus, LogIn, Headphones,
  ChevronDown, Check
} from 'lucide-react';
import { DarkModeToggle } from './DarkModeToggle';
import NotificationBell from './NotificationBell';
import { APP_NAME } from '../config/appConfig';
import { CURRENCIES } from '../utils/currency';
import { CurrencyChooserModal } from './CurrencyChooserModal';
import './Navbar.scss';

export const Navbar = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { selectedCurrency } = useSelector((state) => state.currency);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const hasPublicUserSession = isAuthenticated && user?.role === 'USER';
  const useReferenceHeader = !hasPublicUserSession;

  const [showAuthMenu, setShowAuthMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showCurrencyChooser, setShowCurrencyChooser] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [activeReferenceNav, setActiveReferenceNav] = useState(() => {
    if (location.hash === '#how-it-works') return 'how-it-works';
    if (location.pathname === '/contact') return 'contact';
    return 'home';
  });

  const authRef = useRef(null);
  const langRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (authRef.current && !authRef.current.contains(e.target)) setShowAuthMenu(false);
      if (langRef.current && !langRef.current.contains(e.target)) setShowLangMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (location.pathname === '/contact') setActiveReferenceNav('contact');
    else if (location.pathname === '/' && location.hash === '#how-it-works') setActiveReferenceNav('how-it-works');
    else if (location.pathname === '/' && !location.hash) setActiveReferenceNav('home');
  }, [location.pathname, location.hash]);

  const LANGUAGES = [
    { code: 'en', name: 'English' },
    { code: 'ar', name: 'العربية' },
  ];

  const currentLang = LANGUAGES.find(l => l.code === i18n.language.split('-')[0]) || LANGUAGES[0];

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    dispatch(setLanguage(lang));
    setShowLangMenu(false);
  };

  const handleLogout = async () => {
    setShowAuthMenu(false);
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8020';
    const token = getAccessToken();
    try {
      if (token) {
        await fetch(`${backendUrl}/api/v2/auth/logout`, {
          method: 'POST',
          credentials: 'omit',
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch { /* Always clear local UI state, even if the network is unavailable. */ }
    clearAuthStorage();
    dispatch(api.util.resetApiState());
    dispatch(logout());
    navigate('/');
  };

  const close = () => { setShowAuthMenu(false); setShowMobileMenu(false); setShowCurrencyChooser(false); setShowLangMenu(false); };
  const selectReferenceNav = (item) => {
    setActiveReferenceNav(item);
    close();
  };
  const goToHomepageTop = (event) => {
    event.preventDefault();
    selectReferenceNav('home');
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <nav className={`navbar${useReferenceHeader ? ' navbar--reference' : ' navbar--authenticated'}`} data-testid="navbar">
      <div className="container">
        <div className="navbar__inner">

          {/* Brand */}
          <Link to="/" className="navbar__brand" onClick={useReferenceHeader ? goToHomepageTop : close} data-testid="navbar-brand">
            <img src="/images/logo.png" alt={APP_NAME} className="navbar__logo" />
            <span className="navbar__brand-name">{APP_NAME}</span>
          </Link>

          {/* Mobile toggle */}
          <button
            className="navbar__mobile-toggle"
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {showMobileMenu ? <X size={22} /> : <Menu size={22} />}
          </button>

          {/* Nav links */}
          <div className={`navbar__links${showMobileMenu ? ' navbar__links--open' : ''}`}>
            {useReferenceHeader ? <>
              <Link to="/" className={`navbar__link${location.pathname === '/' && activeReferenceNav === 'home' ? ' navbar__link--active' : ''}`} onClick={goToHomepageTop}>Home</Link>
              <a href="/#how-it-works" className={`navbar__link${activeReferenceNav === 'how-it-works' ? ' navbar__link--active' : ''}`} onClick={() => selectReferenceNav('how-it-works')}>How It Works</a>
              <Link to="/contact" className={`navbar__link${activeReferenceNav === 'contact' ? ' navbar__link--active' : ''}`} onClick={() => selectReferenceNav('contact')}>Contact us</Link>
              <Link to="/become-a-partner" className={`navbar__link${location.pathname === '/become-a-partner' ? ' navbar__link--active' : ''}`} onClick={() => selectReferenceNav('partner')}>Become a Partner</Link>
              <Link
                to={location.pathname === '/become-a-partner' ? '/partner/login' : '/login'}
                className="navbar__cta"
                onClick={close}
              >Get Started</Link>
            </> : <>
            <Link to="/" className="navbar__link" onClick={close} data-testid="nav-home">
              <Home size={16} />
              <span>{t('nav.home')}</span>
            </Link>

            <a href="/#how-it-works" className="navbar__link" onClick={close}>How it works</a>
            <Link to="/contact" className="navbar__link" onClick={close}>Contact us</Link>
            <Link to="/become-a-partner" className="navbar__link" onClick={close}>Become a Partner</Link>

            {/* Currency */}
            <button className="navbar__icon-btn navbar__utility-start" onClick={() => { setShowCurrencyChooser(true); setShowAuthMenu(false); setShowLangMenu(false); }} data-testid="currency-toggle" aria-haspopup="dialog">
              <Coins size={15} /><span>{CURRENCIES.find((currency) => currency.code === selectedCurrency)?.symbol} {selectedCurrency}</span><ChevronDown size={12} style={{ opacity: 0.6 }} />
            </button>
            <CurrencyChooserModal open={showCurrencyChooser} onOpenChange={setShowCurrencyChooser} />

            {/* Language */}
            <div className="navbar__dropdown" ref={langRef}>
              <button
                className="navbar__icon-btn"
                onClick={() => { setShowLangMenu(!showLangMenu); setShowAuthMenu(false); setShowCurrencyChooser(false); }}
                data-testid="lang-toggle"
              >
                <Globe size={15} />
                <span>{currentLang.code.toUpperCase()}</span>
                <ChevronDown size={12} style={{ opacity: 0.6 }} />
              </button>

              {showLangMenu && (
                <div className="navbar__panel" data-testid="lang-menu" style={{ minWidth: 200 }}>
                  <div className="navbar__panel-header">
                    <Globe size={16} />
                    <span>Select Language</span>
                  </div>
                  <div className="navbar__panel-section">
                    {LANGUAGES.map((lang) => (
                      <button
                        key={lang.code}
                        className={`navbar__panel-item${currentLang.code === lang.code ? ' navbar__panel-item--active' : ''}`}
                        onClick={() => changeLanguage(lang.code)}
                        data-testid={`lang-${lang.code}`}
                        style={{ gap: '8px' }}
                      >
                        <span style={{ flex: 1, textAlign: 'start' }}>{lang.name}</span>
                        {currentLang.code === lang.code && <Check className="check" size={16} aria-label="Selected" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Dark mode */}
            <DarkModeToggle />

            {/* Auth */}
            {hasPublicUserSession ? (
              <div className="navbar__dropdown" ref={authRef}>
                <button
                  className="navbar__user-trigger"
                  onClick={() => { setShowAuthMenu(!showAuthMenu); setShowCurrencyChooser(false); }}
                  data-testid="user-menu-toggle"
                  aria-expanded={showAuthMenu}
                >
                  <span className="navbar__user-avatar" aria-hidden="true">
                    {(user?.name || 'Account').split(' ').map(part => part[0]).slice(0, 2).join('').toUpperCase()}
                  </span>
                  <span className="navbar__user-copy">
                    <strong>{user?.name || 'Account'}</strong>
                    <small>{user?.email || 'Guest account'}</small>
                  </span>
                  <ChevronDown size={12} style={{ opacity: 0.6 }} />
                </button>

                {showAuthMenu && (
                  <div className="navbar__panel navbar__panel--user-menu" data-testid="user-menu">
                    <div className="navbar__panel-section">
                      {user?.role === 'USER' && (
                        <>
                          <Link to="/me/profile" className="navbar__panel-item" onClick={close}>
                            <User size={16} /> My Profile
                          </Link>
                          <NotificationBell variant="menu" />
                          <Link to="/me/enquiries" className="navbar__panel-item" onClick={close}>
                            <FileText size={16} /> Enquiries &amp; Bookings
                          </Link>
                          <Link to="/me/support" className="navbar__panel-item" onClick={close}>
                            <Headphones size={16} /> FAQ &amp; Support
                          </Link>
                        </>
                      )}

                      <div className="navbar__panel-divider" />
                      <button className="navbar__panel-item navbar__panel-item--danger" onClick={handleLogout}>
                        <LogOut size={16} /> {t('nav.logout')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="navbar__dropdown navbar__auth-dropdown" ref={authRef}>
                <button
                  className="navbar__cta"
                  onClick={() => { setShowAuthMenu(!showAuthMenu); setShowCurrencyChooser(false); }}
                  data-testid="auth-menu-toggle"
                >
                  {t('nav.loginSignUp')}
                </button>

                {showAuthMenu && (
                  <div className="navbar__panel" data-testid="auth-menu">
                    <div className="navbar__panel-section">
                      <div className="navbar__panel-section-title">{t('nav.signIn')}</div>
                      <Link to="/login" className="navbar__panel-item" onClick={close} data-testid="login-user">
                        <LogIn size={14} /> {t('nav.guestLogin')}
                      </Link>
                    </div>

                    <div className="navbar__panel-divider" />

                    <div className="navbar__panel-section">
                      <div className="navbar__panel-section-title">{t('nav.createAccount')}</div>
                      <Link to="/register" className="navbar__panel-item" onClick={close} data-testid="register-user">
                        <Plus size={14} /> {t('nav.registerAsGuest')}
                      </Link>
                      <Link to="/partner/signup" className="navbar__panel-item" onClick={close} data-testid="register-partner">
                        <Plus size={14} /> {t('nav.registerAsPartner')}
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            )}
            </>}
          </div>
        </div>
      </div>
    </nav>
  );
};